import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faBuilding,
  faIdCard,
  faCog,
  faMapMarkerAlt,
  faFileInvoice,
  faClipboardCheck,
  faTimes,
  faChevronUp,
  faChevronDown,
  faShoppingCart,
  faShip,
  faKey,
  faBalanceScale,
  faBoxes,
  faWeight,
} from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";

const TrafficFilters = ({
  customerOptions,
  divisionOptions,
  rfcOptions,
  operationTypeOptions,
  entryPointOptions,
  pedimentoOptions,
  statusOptions,
  purchaseOrderOptions,
  billOfLadingOptions,
  clavePedOptions,
  regimeOptions,
  trafficStatusOptions,
  supplierOptions,
  weightStats,
  packagesStats,
  selectedCustomers,
  selectedDivisions,
  selectedRfcs,
  selectedOperationTypes,
  selectedEntryPoints,
  selectedPedimentos,
  selectedStatus,
  selectedPurchaseOrders,
  selectedBillOfLadings,
  selectedClavePeds,
  selectedRegimes,
  selectedTrafficStatus,
  selectedSuppliers,
  weightRange,
  packagesRange,
  setSelectedCustomers,
  setSelectedDivisions,
  setSelectedRfcs,
  setSelectedOperationTypes,
  setSelectedEntryPoints,
  setSelectedPedimentos,
  setSelectedStatus,
  setSelectedPurchaseOrders,
  setSelectedBillOfLadings,
  setSelectedClavePeds,
  setSelectedRegimes,
  setSelectedTrafficStatus,
  setSelectedSuppliers,
  setWeightRange,
  setPackagesRange,
  clearAllFilters,
  clearFilterType,
  showFilters,
  setShowFilters,
}) => {
  // Enhanced select styles for a more corporate look with FIXED Z-INDEX
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
      "&:hover": {
        borderColor: "#cbd5e1",
      },
      borderRadius: "0.375rem",
      minHeight: "38px",
      transition: "all 0.2s ease",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#e1effe",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#1e40af",
      fontWeight: 500,
      fontSize: "0.8125rem",
      padding: "0.125rem 0.25rem",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#60a5fa",
      ":hover": {
        backgroundColor: "#bfdbfe",
        color: "#1e3a8a",
      },
      borderRadius: "0 0.25rem 0.25rem 0",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#94a3b8",
      fontSize: "0.875rem",
    }),
    menu: (base) => ({
      ...base,
      // Set a very high z-index to ensure dropdowns appear above all other elements
      zIndex: 9999,
      borderRadius: "0.375rem",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      border: "1px solid #e2e8f0",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
        ? "#e1effe"
        : "white",
      color: state.isSelected ? "white" : "#1e293b",
      fontSize: "0.875rem",
      padding: "0.5rem 0.75rem",
      ":active": {
        backgroundColor: state.isSelected ? "#3b82f6" : "#bfdbfe",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0.25rem 0.5rem",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "#94a3b8",
      ":hover": {
        color: "#64748b",
      },
      padding: "0.25rem",
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "#94a3b8",
      ":hover": {
        color: "#64748b",
      },
      padding: "0.25rem",
    }),
  };

  // Calculate if any filters are active
  const hasActiveFilters =
    selectedCustomers.length > 0 ||
    selectedDivisions.length > 0 ||
    selectedRfcs.length > 0 ||
    selectedOperationTypes.length > 0 ||
    selectedEntryPoints.length > 0 ||
    selectedPedimentos.length > 0 ||
    selectedStatus.length > 0 ||
    selectedPurchaseOrders.length > 0 ||
    selectedBillOfLadings.length > 0 ||
    selectedClavePeds.length > 0 ||
    selectedRegimes.length > 0 ||
    selectedTrafficStatus.length > 0 ||
    selectedSuppliers.length > 0 ||
    (weightRange.min !== "" || weightRange.max !== "") ||
    (packagesRange.min !== "" || packagesRange.max !== "");

  return (
    <div className="bg-white shadow rounded-lg border border-gray-100 overflow-visible transition-all duration-300 ease-in-out relative z-30">
      {/* Filter header with toggle */}
      <div
        className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 cursor-pointer"
        onClick={() => setShowFilters(!showFilters)}
      >
        <div className="flex items-center">
          <div className="bg-blue-50 p-2 rounded-md mr-3">
            <FontAwesomeIcon icon={faFilter} className="text-blue-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-800">
            Advanced Filters
            {hasActiveFilters && (
              <span className="ml-2 bg-blue-600 text-white text-xs font-bold py-1 px-2 rounded-full">
                {selectedCustomers.length +
                  selectedDivisions.length +
                  selectedRfcs.length +
                  selectedOperationTypes.length +
                  selectedEntryPoints.length +
                  selectedPedimentos.length +
                  selectedStatus.length +
                  selectedPurchaseOrders.length +
                  selectedBillOfLadings.length +
                  selectedClavePeds.length +
                  selectedRegimes.length +
                  selectedTrafficStatus.length +
                  selectedSuppliers.length +
                  (weightRange.min !== "" || weightRange.max !== "" ? 1 : 0) +
                  (packagesRange.min !== "" || packagesRange.max !== "" ? 1 : 0)}
              </span>
            )}
          </h2>
        </div>
        <div className="flex items-center">
          {hasActiveFilters && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearAllFilters();
              }}
              className="text-sm text-gray-500 hover:text-red-600 mr-4 transition-colors duration-200 font-medium"
            >
              Clear All
            </button>
          )}
          <FontAwesomeIcon
            icon={showFilters ? faChevronUp : faChevronDown}
            className="text-gray-500"
          />
        </div>
      </div>

      {/* Filter content - fixed overflow to 'visible' to allow dropdowns to show */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          showFilters
            ? "max-h-[2000px] opacity-100"
            : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <div className="p-5 overflow-visible">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="mr-2 text-blue-600"
                />
                Customer
              </label>
              <Select
                options={customerOptions}
                isMulti
                onChange={setSelectedCustomers}
                value={selectedCustomers}
                placeholder="Select Customers"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="mr-2 text-blue-600"
                />
                Division
              </label>
              <Select
                options={divisionOptions}
                isMulti
                onChange={setSelectedDivisions}
                value={selectedDivisions}
                placeholder="Select Divisions"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faIdCard}
                  className="mr-2 text-blue-600"
                />
                RFC
              </label>
              <Select
                options={rfcOptions}
                isMulti
                onChange={setSelectedRfcs}
                value={selectedRfcs}
                placeholder="Select RFCs"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon icon={faCog} className="mr-2 text-blue-600" />
                Operation Type
              </label>
              <Select
                options={operationTypeOptions}
                isMulti
                onChange={setSelectedOperationTypes}
                value={selectedOperationTypes}
                placeholder="Select Operation Types"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faMapMarkerAlt}
                  className="mr-2 text-blue-600"
                />
                Entry Point
              </label>
              <Select
                options={entryPointOptions}
                isMulti
                onChange={setSelectedEntryPoints}
                value={selectedEntryPoints}
                placeholder="Select Entry Points"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faFileInvoice}
                  className="mr-2 text-blue-600"
                />
                Pedimento Number
              </label>
              <Select
                options={pedimentoOptions}
                isMulti
                onChange={setSelectedPedimentos}
                value={selectedPedimentos}
                placeholder="Select Pedimento Numbers"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faClipboardCheck}
                  className="mr-2 text-blue-600"
                />
                Status
              </label>
              <Select
                options={statusOptions}
                isMulti
                onChange={setSelectedStatus}
                value={selectedStatus}
                placeholder="Select Status"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faShoppingCart}
                  className="mr-2 text-blue-600"
                />
                Purchase Order
              </label>
              <Select
                options={purchaseOrderOptions}
                isMulti
                onChange={setSelectedPurchaseOrders}
                value={selectedPurchaseOrders}
                placeholder="Select Purchase Orders"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faShip}
                  className="mr-2 text-blue-600"
                />
                Bill of Lading
              </label>
              <Select
                options={billOfLadingOptions}
                isMulti
                onChange={setSelectedBillOfLadings}
                value={selectedBillOfLadings}
                placeholder="Select Bill of Ladings"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faKey}
                  className="mr-2 text-blue-600"
                />
                Clave Ped
              </label>
              <Select
                options={clavePedOptions}
                isMulti
                onChange={setSelectedClavePeds}
                value={selectedClavePeds}
                placeholder="Select Clave Ped"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faBalanceScale}
                  className="mr-2 text-blue-600"
                />
                Regime
              </label>
              <Select
                options={regimeOptions}
                isMulti
                onChange={setSelectedRegimes}
                value={selectedRegimes}
                placeholder="Select Regime"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faClipboardCheck}
                  className="mr-2 text-blue-600"
                />
                Traffic Status
              </label>
              <Select
                options={trafficStatusOptions}
                isMulti
                onChange={setSelectedTrafficStatus}
                value={selectedTrafficStatus}
                placeholder="Select Traffic Status"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="mr-2 text-blue-600"
                />
                Supplier
              </label>
              <Select
                options={supplierOptions}
                isMulti
                onChange={setSelectedSuppliers}
                value={selectedSuppliers}
                placeholder="Select Suppliers"
                classNamePrefix="select-filter"
                className="text-sm"
                styles={customSelectStyles}
              />
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faWeight}
                  className="mr-2 text-blue-600"
                />
                Weight Range
              </label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder={`Min (${weightStats.min.toLocaleString()})`}
                  value={weightRange.min}
                  onChange={(e) => setWeightRange(prev => ({ ...prev, min: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder={`Max (${weightStats.max.toLocaleString()})`}
                  value={weightRange.max}
                  onChange={(e) => setWeightRange(prev => ({ ...prev, max: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="filter-group">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faBoxes}
                  className="mr-2 text-blue-600"
                />
                Packages Range
              </label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder={`Min (${packagesStats.min})`}
                  value={packagesRange.min}
                  onChange={(e) => setPackagesRange(prev => ({ ...prev, min: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="number"
                  placeholder={`Max (${packagesStats.max})`}
                  value={packagesRange.max}
                  onChange={(e) => setPackagesRange(prev => ({ ...prev, max: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active filters display */}
      {hasActiveFilters && (
        <div
          className={`px-5 py-3 bg-gray-50 border-t border-gray-100 transition-all duration-300 ${
            showFilters ? "opacity-100" : "opacity-0 hidden"
          }`}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-600 mr-1">
              Active filters:
            </span>

            {selectedCustomers.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">Customer:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCustomers((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedDivisions.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">Division:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedDivisions((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedRfcs.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">RFC:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRfcs((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedOperationTypes.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">Op. Type:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOperationTypes((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedEntryPoints.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">Entry:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedEntryPoints((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedPedimentos.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">Pedimento:</span>{" "}
                {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPedimentos((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedStatus.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">Status:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedStatus((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedPurchaseOrders.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">PO:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPurchaseOrders((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedBillOfLadings.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">BOL:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedBillOfLadings((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedClavePeds.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">Clave:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedClavePeds((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedRegimes.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">Regime:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedRegimes((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedTrafficStatus.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">Traffic:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTrafficStatus((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {selectedSuppliers.map((item) => (
              <span
                key={item.value}
                className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100"
              >
                <span className="font-medium mr-1">Supplier:</span> {item.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedSuppliers((prev) =>
                      prev.filter((i) => i.value !== item.value)
                    );
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            ))}

            {(weightRange.min !== "" || weightRange.max !== "") && (
              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100">
                <span className="font-medium mr-1">Weight:</span> 
                {weightRange.min || '0'} - {weightRange.max || '∞'}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setWeightRange({ min: "", max: "" });
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            )}

            {(packagesRange.min !== "" || packagesRange.max !== "") && (
              <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full flex items-center text-xs transition-all hover:bg-blue-100">
                <span className="font-medium mr-1">Packages:</span> 
                {packagesRange.min || '0'} - {packagesRange.max || '∞'}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPackagesRange({ min: "", max: "" });
                  }}
                  className="ml-1 text-blue-500 hover:text-blue-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              </span>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                clearAllFilters();
              }}
              className="ml-2 text-red-600 hover:text-red-700 text-xs font-medium underline transition-colors"
            >
              Clear all
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrafficFilters;
